'use client';

import { useSession } from '@shared/auth';
import { Button } from '@shared/components/shadcn';
import { PLAY_ROUTE, useHoverRoutePrefetch } from '@shared/hooks';
import { ProfileAlt } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import { BIG_WINNER_POD_CONTAINER_CLASS } from './constants';

type BigWinnerPodProps = Omit<ComponentProps<typeof Link>, 'children' | 'className' | 'draggable'> & {
  logo: string | null;
  alt: string;
  multiplier: string;
  amount: string;
  playerName: string;
};

/**
 * BigWinnerPod component displays a big winner pod with relevant information such as game logo, multiplier, amount, and username.
 * Component is a link to the game page.
 *
 * @param logo - The logo of the game.
 * @param multiplier - The multiplier of the big winner.
 * @param amount - The amount of the big winner.
 * @param username - The username of the big winner pod.
 * @returns A big winner pod as styled link to the game page.
 */
function BigWinnerPod({ logo, multiplier, amount, playerName, alt, ...props }: BigWinnerPodProps) {
  const t = useTranslations('casino');
  const { session } = useSession();
  const routePrefetch = useHoverRoutePrefetch({
    href: String(props.href),
    isAuthenticated: session.status === 'authenticated',
    authPathMatch: PLAY_ROUTE,
  });

  const { cContainer, cLogoContainer, cLogo, cDetailsContainer, cMultiplier, cAmount, cUserContainer, cPlayButton } = {
    cContainer: twMerge(
      BIG_WINNER_POD_CONTAINER_CLASS,
      'dark group/big-winner py-1 px-3 bg-secondary flex items-center gap-3 border-b border-border-accent-yellow cursor-pointer select-none bg-[radial-gradient(73.08%_76.04%_at_50%_0%,rgba(255,245,0,0.2)_0%,rgba(255,245,0,0)_100%)]'
    ),
    cLogoContainer: 'size-14 flex items-center justify-center',
    cLogo: 'size-full object-contain',
    cDetailsContainer: 'paragraph-s-strong flex flex-col justify-start group-hover/big-winner:hidden',
    cMultiplier: 'text-accent-yellow',
    cAmount: 'text-accent-green',
    cUserContainer: 'flex items-center gap-0.5 text-foreground-subtle',
    cPlayButton: 'group-hover/big-winner:block hidden',
  };

  return (
    <Link {...props} {...routePrefetch} draggable={false} className={cContainer}>
      <div className={cLogoContainer}>{logo ? <Image src={logo} alt={alt} width={56} height={56} draggable={false} className={cLogo} /> : null}</div>
      <div className={cDetailsContainer}>
        <span className={cMultiplier}>{multiplier}</span>
        <span className={cAmount}>{amount}</span>
        <div className={cUserContainer}>
          <ProfileAlt size="tiny" />
          <span>{playerName}</span>
        </div>
      </div>
      <Button asChild variant="primary" size="s" className={cPlayButton}>
        <div>{t('play')}</div>
      </Button>
    </Link>
  );
}

export { BigWinnerPod };
